import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

const OTHER = "__other__";
const blank = { plate: "", model: "", station_id: "", contract_type: "LT", provider: "", status: "DISPONIBILE", reason: "", notes: "", workshop: "", transfer_station_id: null, transfer_other: "" };

const NONE = "__none";
const Pick = ({ label, value, onChange, options, testId, placeholder, allowNone, disabled }) => (
  <div className="space-y-1.5"><Label>{label}</Label>
    <Select value={value || (allowNone ? NONE : undefined)} onValueChange={(v) => onChange(v === NONE ? "" : v)} disabled={disabled}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder || "Seleziona"} /></SelectTrigger>
      <SelectContent>{(allowNone ? [[NONE, "—"], ...options] : options).map(([v, l]) => <SelectItem key={v} value={v} data-testid={`${testId}-opt-${v}`}>{l}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

export const VehicleDialog = ({ vehicle, isAdmin, stations, meta, workshops, onClose, onSaved }) => {
  const [f, setF] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (vehicle) setF({ ...blank, ...vehicle, definitive: false, transfer_station_id: vehicle.transfer_station_id || (vehicle.transfer_other ? OTHER : null) });
  }, [vehicle]);
  if (!vehicle) return null;
  const set = (k) => (v) => {
    const val = v?.target ? v.target.value : v;
    setF({ ...f, [k]: val, ...(k === "status" && val === "DISPONIBILE" ? { reason: "" } : {}), ...(k === "dest_status" && val === "DISPONIBILE" ? { dest_reason: "" } : {}) });
  };
  const isNew = !vehicle.id;
  const full = isAdmin;

  const save = async () => {
    const other = f.transfer_station_id === OTHER;
    const status = { status: f.status, reason: f.reason, notes: f.notes, workshop: f.workshop,
      transfer_station_id: other ? null : f.transfer_station_id, transfer_other: other ? f.transfer_other : "",
      definitive: f.status === "TRASFERITO" && !other && !!f.transfer_station_id && !!f.definitive,
      dest_status: f.status === "TRASFERITO" && !other ? f.dest_status || "DISPONIBILE" : null, dest_reason: f.dest_reason || "" };
    if (status.definitive && !window.confirm(`Confermi il trasferimento DEFINITIVO di ${f.plate || vehicle.plate} a ${destName}? Il mezzo passerà alla flotta della nuova sede.`)) return;
    setSaving(true);
    try {
      if (vehicle.__dest) await api.put(`/fleet/vehicles/${vehicle.id}/destination`, { dest_status: f.dest_status || "DISPONIBILE", dest_reason: f.dest_reason || "" });
      else if (isNew) await api.post("/fleet/vehicles", { ...f, ...status });
      else if (full) await api.put(`/fleet/vehicles/${vehicle.id}`, { ...f, ...status });
      else await api.put(`/fleet/vehicles/${vehicle.id}/status`, status);
      toast.success("Mezzo salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const destOptions = [...meta.destinations.filter((s) => s.id !== f.station_id).map((s) => [s.id, s.name]), [OTHER, "Altra sede..."]];
  const destName = meta.destinations.find((s) => s.id === f.transfer_station_id)?.name;
  const destStatuses = Object.entries(meta.statuses).filter(([k]) => k !== "TRASFERITO");
  const destFields = (
    <>
      <Pick label={`Stato presso ${destName || "destinazione"}`} value={f.dest_status || "DISPONIBILE"} onChange={set("dest_status")} options={destStatuses} testId="fleet-dest-status-select" />
      {(f.dest_status || "DISPONIBILE") !== "DISPONIBILE" && <Pick label="Motivo presso destinazione" value={f.dest_reason} onChange={set("dest_reason")} options={meta.reasons.map((r) => [r, r])} allowNone testId="fleet-dest-reason-select" />}
    </>
  );

  if (vehicle.__dest) return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fleet-dest-dialog">
        <DialogHeader>
          <DialogTitle>Mezzo ricevuto {vehicle.plate}</DialogTitle>
          <DialogDescription>Da {vehicle.station_name} · lo stato qui sotto viene conteggiato nella tua sede.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">{destFields}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="fleet-dest-save-button">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="fleet-vehicle-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nuovo mezzo" : `Mezzo ${vehicle.plate}`}</DialogTitle>
          {!full && <DialogDescription>{vehicle.model} · {meta.contracts[vehicle.contract_type]}</DialogDescription>}
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          {full && (
            <>
              <div className="space-y-1.5"><Label>Targa</Label><Input value={f.plate} onChange={set("plate")} data-testid="fleet-plate-input" /></div>
              <div className="space-y-1.5"><Label>Modello</Label><Input value={f.model} onChange={set("model")} data-testid="fleet-model-input" /></div>
              <Pick label="Stazione" value={f.station_id} onChange={set("station_id")} options={stations.map((s) => [s.id, s.name])} testId="fleet-station-select" />
              <Pick label="Contratto" value={f.contract_type} onChange={set("contract_type")} options={Object.entries(meta.contracts)} testId="fleet-contract-select" />
              <div className="space-y-1.5 sm:col-span-2"><Label>Società noleggio / leasing</Label><Input value={f.provider} onChange={set("provider")} data-testid="fleet-provider-input" /></div>
            </>
          )}
          <Pick label="Stato mezzo" value={f.status} onChange={set("status")} options={Object.entries(meta.statuses)} testId="fleet-status-select" />
          <Pick label="Motivo indisponibilità" value={f.status === "DISPONIBILE" ? "" : f.reason} onChange={set("reason")} options={meta.reasons.map((r) => [r, r])} allowNone disabled={f.status === "DISPONIBILE"} testId="fleet-reason-select" />
          {f.status === "TRASFERITO" && (
            <>
              <Pick label="Trasferito a" value={f.transfer_station_id} onChange={set("transfer_station_id")} options={destOptions} testId="fleet-transfer-select" />
              {f.transfer_station_id === OTHER && <div className="space-y-1.5"><Label>Nome altra sede</Label><Input value={f.transfer_other} onChange={set("transfer_other")} placeholder="es. OLZ5" data-testid="fleet-transfer-other-input" /></div>}
              {f.transfer_station_id && f.transfer_station_id !== OTHER && (
                <label className="flex items-start gap-3 rounded-lg border border-orange-200 bg-orange-50 p-3 text-sm sm:col-span-2">
                  <Checkbox checked={!!f.definitive} onCheckedChange={(v) => setF({ ...f, definitive: !!v })} data-testid="fleet-definitive-checkbox" className="mt-0.5" />
                  <span><b className="font-semibold text-orange-800">Trasferimento definitivo</b><span className="block text-xs text-orange-700">Il mezzo passa alla flotta di {destName} con lo stato indicato sotto. Senza flag resta della sede attuale (conteggiato come Trasferito) e a {destName} viene conteggiato con lo stato indicato sotto.</span></span>
                </label>
              )}
              {f.transfer_station_id && f.transfer_station_id !== OTHER && destFields}
            </>
          )}
          <div className="space-y-1.5 sm:col-span-2"><Label>Officina / luogo</Label>
            <Input list="fleet-workshops" value={f.workshop} onChange={set("workshop")} placeholder="Scegli o scrivi una nuova officina" data-testid="fleet-workshop-input" />
            <datalist id="fleet-workshops">{workshops.map((w) => <option key={w.id} value={w.name} />)}</datalist>
          </div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Descrizione</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="fleet-notes-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || (full && (!f.plate.trim() || !f.station_id))} data-testid="fleet-save-button">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
